/**
 * API Configuration - Environment Variable Priority
 *
 * This configuration prioritizes environment variables over dynamic URL construction.
 * Falls back to dynamic construction only if environment variables are not set.
 */

// Determine if we're in development or production
const isDevelopment = import.meta.env.DEV;

// Get the current origin (protocol + domain) - used only as fallback
const getCurrentOrigin = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
};

// Get the API base URL - prioritize environment variables
const getApiBaseUrl = (): string => {
  // In production, check for hardcoded patterns first to avoid dynamic construction issues
  if (!isDevelopment) {
    const origin = getCurrentOrigin();
    if (origin && origin.includes('prismaticforge.ai')) {
      return 'https://api.prismaticforge.ai';
    }
    if (origin && origin.includes('prismaticforge.com')) {
      return 'https://api.prismaticforge.com';
    }
  }

  // Check environment variables (primarily for development or explicit overrides)
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // Development fallback
  if (isDevelopment) {
    return 'http://localhost:8000';
  }

  // Final production fallback
  return 'https://api.prismaticforge.ai';
};

// Static mapping of env vars (Vite requires static access to import.meta.env)
const ENV_VARS: Record<string, string | undefined> = {
  VITE_AUTH_SERVICE_URL: import.meta.env.VITE_AUTH_SERVICE_URL,
  VITE_USER_SERVICE_URL: import.meta.env.VITE_USER_SERVICE_URL,
  VITE_AGENT_SERVICE_URL: import.meta.env.VITE_AGENT_SERVICE_URL,
  VITE_CONVERSATION_SERVICE_URL: import.meta.env.VITE_CONVERSATION_SERVICE_URL,
  VITE_KNOWLEDGE_SERVICE_URL: import.meta.env.VITE_KNOWLEDGE_SERVICE_URL,
  VITE_CONTEXT_SERVICE_URL: import.meta.env.VITE_CONTEXT_SERVICE_URL,
  VITE_INTEGRATION_SERVICE_URL: import.meta.env.VITE_INTEGRATION_SERVICE_URL,
  VITE_COLLABORATION_SERVICE_URL: import.meta.env.VITE_COLLABORATION_SERVICE_URL,
  VITE_DOCUMENT_SERVICE_URL: import.meta.env.VITE_DOCUMENT_SERVICE_URL,
  VITE_MEP_TOOLKIT_SERVICE_URL: import.meta.env.VITE_MEP_TOOLKIT_SERVICE_URL,
  VITE_TODO_SERVICE_URL: import.meta.env.VITE_TODO_SERVICE_URL,
  VITE_BILLING_SERVICE_URL: import.meta.env.VITE_BILLING_SERVICE_URL,
  VITE_WORKFLOW_SERVICE_URL: import.meta.env.VITE_WORKFLOW_SERVICE_URL,
  VITE_CONVERSATION_WS_URL: import.meta.env.VITE_CONVERSATION_WS_URL,
  VITE_COLLABORATION_WS_URL: import.meta.env.VITE_COLLABORATION_WS_URL,
};

// Get service-specific URL - prioritize environment variable over base URL
const getServiceUrl = (envVarName: string): string => {
  const envValue = ENV_VARS[envVarName];
  if (envValue) {
    return envValue;
  }

  // Fall back to base URL (which already prioritizes environment variables)
  const baseUrl = getApiBaseUrl();
  return baseUrl;
};

// WebSocket URL helper
const getWebSocketUrl = (envVarName: string): string => {
  // In production, always use the API subdomain regardless of environment variables
  if (!isDevelopment) {
    const origin = getCurrentOrigin();
    if (origin && origin.includes('prismaticforge.ai')) {
      return 'wss://api.prismaticforge.ai';
    }
    if (origin && origin.includes('prismaticforge.com')) {
      return 'wss://api.prismaticforge.com';
    }
  }

  // Check for WebSocket-specific environment variable (development only)
  const envValue = ENV_VARS[envVarName];
  if (envValue) {
    return envValue;
  }

  // Development fallback - convert HTTP base URL to WebSocket URL
  if (isDevelopment) {
    const baseUrl = getApiBaseUrl();
    if (baseUrl) {
      const wsUrl = baseUrl.replace(/^https?:/, baseUrl.startsWith('https') ? 'wss:' : 'ws:');
      return wsUrl;
    }
  }

  // Final fallback - explicitly use API subdomain
  return 'wss://api.prismaticforge.ai';
};

// Export configuration
export const API_CONFIG = {
  // Base URL for general API calls
  BASE_URL: getApiBaseUrl(),

  // Service-specific URLs - all prioritize environment variables
  AUTH_SERVICE_URL: getServiceUrl('VITE_AUTH_SERVICE_URL'),
  USER_SERVICE_URL: getServiceUrl('VITE_USER_SERVICE_URL'),
  AGENT_SERVICE_URL: getServiceUrl('VITE_AGENT_SERVICE_URL'),
  CONVERSATION_SERVICE_URL: getServiceUrl('VITE_CONVERSATION_SERVICE_URL'),
  KNOWLEDGE_SERVICE_URL: getServiceUrl('VITE_KNOWLEDGE_SERVICE_URL'),
  CONTEXT_SERVICE_URL: getServiceUrl('VITE_CONTEXT_SERVICE_URL'),
  INTEGRATION_SERVICE_URL: getServiceUrl('VITE_INTEGRATION_SERVICE_URL'),
  COLLABORATION_SERVICE_URL: getServiceUrl('VITE_COLLABORATION_SERVICE_URL'),
  DOCUMENT_SERVICE_URL: getServiceUrl('VITE_DOCUMENT_SERVICE_URL'),
  MEP_SERVICE_URL: getServiceUrl('VITE_MEP_TOOLKIT_SERVICE_URL'),
  TODO_SERVICE_URL: getServiceUrl('VITE_TODO_SERVICE_URL'),
  BILLING_SERVICE_URL: getServiceUrl('VITE_BILLING_SERVICE_URL'),
  WORKFLOW_SERVICE_URL: getServiceUrl('VITE_WORKFLOW_SERVICE_URL'),

  // WebSocket URLs - prioritize environment variables
  WS_URL: getWebSocketUrl('VITE_CONVERSATION_WS_URL'),
  COLLABORATION_WS_URL: getWebSocketUrl('VITE_COLLABORATION_WS_URL'),

  // Helper function to get the current API base URL
  getApiUrl: (path: string = ''): string => {
    const baseUrl = getApiBaseUrl();
    if (!baseUrl) return path;
    return `${baseUrl}${path}`;
  },

  // Helper function to get service-specific URL
  getServiceApiUrl: (serviceName: string, path: string = ''): string => {
    const baseUrl = getApiBaseUrl();
    if (!baseUrl) return path;
    return `${baseUrl}${path}`;
  },

  // Check if we're in development
  isDevelopment,

  // Get current origin
  getCurrentOrigin,

  // Environment variable validation
  validate: (): { isValid: boolean; missingVars: string[]; warnings: string[] } => {
    const missingVars: string[] = [];
    const warnings: string[] = [];

    // Check critical environment variables
    if (!import.meta.env.VITE_API_BASE_URL && !isDevelopment) {
      missingVars.push('VITE_API_BASE_URL');
    }

    // Check service-specific URLs (warnings only, as they fall back to base URL)
    const serviceEnvVars = [
      'VITE_AUTH_SERVICE_URL',
      'VITE_USER_SERVICE_URL',
      'VITE_AGENT_SERVICE_URL',
      'VITE_CONVERSATION_SERVICE_URL',
      'VITE_KNOWLEDGE_SERVICE_URL',
      'VITE_CONTEXT_SERVICE_URL',
      'VITE_INTEGRATION_SERVICE_URL',
      'VITE_COLLABORATION_SERVICE_URL',
      'VITE_DOCUMENT_SERVICE_URL',
      'VITE_MEP_TOOLKIT_SERVICE_URL',
      'VITE_TODO_SERVICE_URL',
      'VITE_BILLING_SERVICE_URL',
    ];

    serviceEnvVars.forEach(envVar => {
      if (!ENV_VARS[envVar] && !isDevelopment) {
        warnings.push(`${envVar} not set, using base URL fallback`);
      }
    });

    return {
      isValid: missingVars.length === 0,
      missingVars,
      warnings,
    };
  },
};

// Validate configuration and log results
if (typeof window !== 'undefined') {
  const validation = API_CONFIG.validate();

  if (isDevelopment) {
    console.log('[API Config] Running in development mode');
    console.log('[API Config] Base URL:', API_CONFIG.BASE_URL);
  } else {
    console.log('[API Config] Running in production mode');
    console.log('[API Config] Environment variables validation:', validation);

    if (!validation.isValid) {
      console.error('[API Config] Missing critical environment variables:', validation.missingVars);
    }

    if (validation.warnings.length > 0) {
      console.warn('[API Config] Configuration warnings:', validation.warnings);
    }
  }
}

export default API_CONFIG;
